import React, { useEffect, useState, useMemo } from 'react';
import { Button } from 'react-bootstrap';
import { FaCheckCircle, FaClock, FaEdit, FaQuestionCircle } from 'react-icons/fa';
import { doc, onSnapshot } from 'firebase/firestore';
import ContentUnitTag from '../common/ContentUnitTag';
import QnAStatusTag from './QnAStatusTag';
import ContentDetailHero from '../../platform/ContentDetailHero';
import QnAAnswerForm from './QnAAnswerForm';
import QnAFollowUpForm from './QnAFollowUpForm';
import { db } from '../../firebase/firebase-config';
import { useUser } from '../../context/UserContext';
import { useTenant } from '../../context/TenantContext';
import { useMembership } from '../../context/MembershipContext';
import QnAQuestion from '../../models/QnAQuestion';
import { COLLECTIONS } from '../../platform/constants';
import {
  closeQuestion,
  getOfficialAnswers,
  markQuestionAsRead,
  subscribeQuestionThread,
} from '../../services/questionsService';
import {
  QNA_ANSWER_TYPE,
  QNA_ANSWER_TYPE_LABELS,
  QNA_CLOSE_REMINDER_MESSAGE,
  QNA_STATUS,
} from '../../platform/qnaConstants';
import { useConfirm } from '../../platform/confirm/ConfirmProvider';
import { useToast } from '../../platform/toast/ToastProvider';
import {
  canAddFollowUp,
  canAnswerQuestion,
  canCloseQuestion,
} from '../../platform/qnaPermissions';
import { isRichTextHtml, toDisplayHtml } from '../../platform/richTextEditor';
import {
  isDetailHeroEnabled,
  PREMIUM_MODULE_IDS,
  premiumDetailClassName,
} from '../../platform/contentListVisual';
import '../../platform/platform-content-detail.css';
import './QnADetail.css';

function formatDate(date) {
  if (!date) return '';
  const value = date instanceof Date ? date : new Date(date);
  return value.toLocaleString('sl-SI', {
    day: 'numeric',
    month: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function getExtendedBody(question) {
  const title = question?.title?.trim() || '';
  const body = question?.body?.trim() || '';

  if (!body || body === title) {
    return null;
  }

  return {
    content: body,
    isHtml: isRichTextHtml(body),
  };
}

function sortThreadEntries(items) {
  return [...items].sort(
    (a, b) => (a.createdAt?.getTime?.() || 0) - (b.createdAt?.getTime?.() || 0),
  );
}

function getTimelineSortTime(entry) {
  return entry.createdAt?.getTime?.() || 0;
}

function sortConversationTimeline(items) {
  return [...items].sort(
    (a, b) => getTimelineSortTime(a) - getTimelineSortTime(b),
  );
}

function RichTextBlock({ html, className = '' }) {
  return (
    <div
      className={`platform-content-detail__content ${className}`.trim()}
      dangerouslySetInnerHTML={{ __html: html }}
    />
  );
}

function PlainTextBlock({ text, className = '' }) {
  return (
    <div className={`platform-content-detail__content ${className}`.trim()}>
      {text.split('\n').map((line, index) => (
        <p key={`${line}-${index}`}>{line || '\u00A0'}</p>
      ))}
    </div>
  );
}

function AnswerContent({ text, className = '' }) {
  if (isRichTextHtml(text)) {
    return <RichTextBlock html={toDisplayHtml(text)} className={className} />;
  }
  return <PlainTextBlock text={text} className={className} />;
}

function hasUnansweredFollowUps(followUps, question) {
  if (question?.status === QNA_STATUS.CLOSED) {
    return false;
  }

  const answeredAt = question?.answeredAt?.getTime?.() || 0;
  return followUps.some((entry) => {
    const followUpAt = entry.createdAt?.getTime?.() || 0;
    return !answeredAt || followUpAt > answeredAt;
  });
}

function getThreadEntryLabel(entry, followUpIndex) {
  if (entry.type === QNA_ANSWER_TYPE.OFFICIAL) {
    return QNA_ANSWER_TYPE_LABELS[QNA_ANSWER_TYPE.OFFICIAL];
  }
  if (entry.type === QNA_ANSWER_TYPE.FOLLOWUP) {
    return followUpIndex
      ? `Dodatno vprašanje ${followUpIndex}`
      : QNA_ANSWER_TYPE_LABELS[QNA_ANSWER_TYPE.FOLLOWUP];
  }
  return QNA_ANSWER_TYPE_LABELS[entry.type] || QNA_ANSWER_TYPE_LABELS[QNA_ANSWER_TYPE.LEGACY];
}

function ThreadTimelineIcon({ entry }) {
  if (entry.type === QNA_ANSWER_TYPE.OFFICIAL) {
    return <FaCheckCircle aria-hidden="true" />;
  }
  return <FaQuestionCircle aria-hidden="true" />;
}

function ThreadEntry({
  entry,
  followUpIndex = null,
  isLast = false,
  onEdit = null,
}) {
  const isFollowUp = entry.type === QNA_ANSWER_TYPE.FOLLOWUP;
  const isOfficial = entry.type === QNA_ANSWER_TYPE.OFFICIAL;
  const label = getThreadEntryLabel(entry, followUpIndex);

  return (
    <article
      className={[
        'qna-detail__timeline-item',
        isFollowUp && 'qna-detail__timeline-item--followup',
        isOfficial && 'qna-detail__timeline-item--official',
        isLast && 'qna-detail__timeline-item--last',
      ].filter(Boolean).join(' ')}
    >
      <div
        className={[
          'qna-detail__timeline-marker',
          isOfficial && 'qna-detail__timeline-marker--official',
        ].filter(Boolean).join(' ')}
        aria-hidden="true"
      >
        <ThreadTimelineIcon entry={entry} />
      </div>
      <div className="qna-detail__timeline-bubble">
        <div className="qna-detail__timeline-bubble-header">
          <div className="qna-detail__timeline-bubble-title-row">
            <span className="qna-detail__timeline-type">{label}</span>
            {onEdit && (
              <Button
                variant="outline-secondary"
                size="sm"
                className="qna-detail__timeline-edit"
                onClick={onEdit}
              >
                <FaEdit aria-hidden="true" /> Popravi
              </Button>
            )}
          </div>
          <span className="qna-detail__timeline-meta">
            {entry.displayName || 'Anonimen'}
            {entry.createdAt && (
              <>
                <span className="qna-detail__timeline-meta-separator">·</span>
                <time dateTime={entry.createdAt.toISOString()}>{formatDate(entry.createdAt)}</time>
              </>
            )}
          </span>
        </div>
        <AnswerContent text={entry.text} className="qna-detail__timeline-content" />
      </div>
    </article>
  );
}

function OfficialAnswerCard({ answer, onEdit, useHero = false }) {
  return (
    <div className={`platform-content-detail-card qna-detail__answer-card${useHero ? ' qna-detail__answer-card--premium' : ''}`}>
      <div className="platform-content-detail-card__header">
        <h2 className="platform-content-detail-card__title">Odgovor sindikata</h2>
        {onEdit && (
          <Button variant="outline-secondary" size="sm" onClick={onEdit}>
            <FaEdit aria-hidden="true" /> Popravi
          </Button>
        )}
      </div>
      <div className="platform-content-detail__body qna-detail__answer-card-body">
        <div className="qna-detail__answer-meta">
          <span>{answer.displayName || 'Anonimen'}</span>
          {answer.createdAt && (
            <>
              <span className="qna-detail__ticket-meta-separator">·</span>
              <time dateTime={answer.createdAt.toISOString()}>{formatDate(answer.createdAt)}</time>
            </>
          )}
        </div>
        <AnswerContent text={answer.text} className="qna-detail__answer-content" />
      </div>
    </div>
  );
}

function QnAStatusBanner({
  variant = 'pending',
  icon: Icon = FaClock,
  title,
  subtitle,
  compact = false,
}) {
  return (
    <div
      className={[
        'qna-detail-banner',
        `qna-detail-banner--${variant}`,
        compact && 'qna-detail-banner--compact',
      ].filter(Boolean).join(' ')}
      role="status"
    >
      <div className="qna-detail-banner__glow" aria-hidden="true" />
      {!compact && (
        <div className="qna-detail-banner__icon">
          <Icon aria-hidden="true" />
        </div>
      )}
      <div className="qna-detail-banner__body">
        <p className="qna-detail-banner__title">{title}</p>
        {subtitle ? (
          <p className="qna-detail-banner__subtitle">{subtitle}</p>
        ) : null}
      </div>
    </div>
  );
}

function PendingAnswerNotice({ isAuthor, standalone = false, hasFollowUps = false }) {
  const title = isAuthor ? 'Vprašanje poslano' : 'Čaka na odgovor';
  const subtitle = isAuthor
    ? hasFollowUps
      ? 'Vaše dodatno vprašanje je posredovano sindikatu. Odgovor prejmete v tej objavi.'
      : 'Vaše vprašanje je posredovano sindikatu. Odgovor prejmete v tej objavi.'
    : hasFollowUps
      ? 'Dodatno vprašanje čaka na uradni odgovor sindikata.'
      : 'Vprašanje čaka na uradni odgovor sindikata.';

  if (standalone) {
    return (
      <QnAStatusBanner
        variant="pending"
        icon={FaClock}
        title={title}
        subtitle={subtitle}
      />
    );
  }

  return (
    <article className="qna-detail__timeline-item qna-detail__timeline-item--pending qna-detail__timeline-item--last">
      <div className="qna-detail__timeline-marker qna-detail__timeline-marker--pending" aria-hidden="true">
        <FaClock aria-hidden="true" />
      </div>
      <QnAStatusBanner
        variant="pending"
        icon={FaClock}
        title={title}
        subtitle={subtitle}
        compact
      />
    </article>
  );
}

function CloseReminderNotice() {
  return (
    <QnAStatusBanner
      variant="answered"
      icon={FaCheckCircle}
      title={QNA_CLOSE_REMINDER_MESSAGE}
    />
  );
}

function QnADetail({ questionId, selectedQuestion }) {
  const [answers, setAnswers] = useState([]);
  const [question, setQuestion] = useState(selectedQuestion);
  const [closing, setClosing] = useState(false);
  const [editingAnswer, setEditingAnswer] = useState(null);
  const { user } = useUser();
  const { tenantId } = useTenant();
  const { memberships } = useMembership();

  useEffect(() => {
    setQuestion(selectedQuestion);
  }, [selectedQuestion]);

  useEffect(() => {
    setEditingAnswer(null);
  }, [questionId]);

  useEffect(() => {
    if (!questionId) return undefined;

    return onSnapshot(
      doc(db, COLLECTIONS.QUESTIONS, questionId),
      (snapshot) => {
        if (snapshot.exists()) {
          setQuestion(QnAQuestion.fromFirestore(snapshot));
        }
      },
      (error) => console.error('[qna] Failed to load question', error),
    );
  }, [questionId]);

  useEffect(() => {
    if (!questionId || !tenantId) return undefined;

    return subscribeQuestionThread(
      tenantId,
      questionId,
      setAnswers,
      (error) => console.error('[qna] Failed to load question thread', error),
    );
  }, [questionId, tenantId]);

  useEffect(() => {
    if (!user?.uid || !questionId) return;
    markQuestionAsRead(user.uid, questionId);
  }, [user?.uid, questionId, answers.length]);

  const officialAnswers = useMemo(
    () => getOfficialAnswers(answers),
    [answers],
  );
  const officialAnswer = officialAnswers[officialAnswers.length - 1] || null;
  const canAnswer = canAnswerQuestion(memberships, tenantId, user, question);
  const showFollowUpForm = canAddFollowUp(user, question);
  const extendedBody = getExtendedBody(question);
  const isAuthor = user?.uid && question?.uid === user.uid;
  const showPendingAnswer = question?.status === QNA_STATUS.OPEN && !officialAnswer;

  const memberThread = useMemo(
    () => sortThreadEntries(
      answers.filter((entry) => entry.type === QNA_ANSWER_TYPE.FOLLOWUP),
    ),
    [answers],
  );

  const conversationTimeline = useMemo(() => {
    if (!memberThread.length) {
      return [];
    }

    return sortConversationTimeline([...memberThread, ...officialAnswers]);
  }, [memberThread, officialAnswers]);

  const showOfficialCard = Boolean(officialAnswer && memberThread.length === 0);

  const followUpIndexByKey = useMemo(() => {
    let count = 0;
    const map = new Map();
    memberThread.forEach((entry) => {
      count += 1;
      map.set(`${entry.source}-${entry.id}`, count);
    });
    return map;
  }, [memberThread]);

  const waitingForAnswer = hasUnansweredFollowUps(memberThread, question);
  const showCloseAction = canCloseQuestion(user, question, {
    hasOfficialAnswer: Boolean(officialAnswer),
    waitingForAnswer,
  });
  const showPendingInTimeline = memberThread.length > 0 && (showPendingAnswer || waitingForAnswer);
  const showPendingStandalone = showPendingAnswer && memberThread.length === 0;
  const showConversation = memberThread.length > 0;
  const followUpCount = memberThread.length;
  const showAnswerForm = canAnswer && question?.status !== QNA_STATUS.CLOSED
    && !editingAnswer
    && (!officialAnswer || waitingForAnswer || showPendingAnswer);
  const isConversationReply = memberThread.length > 0 && Boolean(officialAnswer);
  const canEditOfficial = canAnswer && question?.status !== QNA_STATUS.CLOSED;
  const showCloseReminder = showCloseAction;

  const confirm = useConfirm();
  const toast = useToast();

  const handleClose = async () => {
    const confirmed = await confirm({
      title: 'Zaključi vprašanje',
      message: 'Ali ste prepričani, da želite zaključiti to vprašanje?',
      confirmLabel: 'Zaključi',
      variant: 'danger',
    });
    if (!confirmed) return;

    setClosing(true);
    try {
      await closeQuestion(questionId);
      if (user?.uid) {
        await markQuestionAsRead(user.uid, questionId);
      }
      toast.success('Vprašanje je bilo zaključeno.');
    } catch (error) {
      console.error('[qna] Failed to close question', error);
      toast.error('Vprašanja ni bilo mogoče zaključiti.');
    } finally {
      setClosing(false);
    }
  };

  if (!question) {
    return null;
  }

  const useHero = isDetailHeroEnabled(PREMIUM_MODULE_IDS.QNA);

  const heroMeta = (
    <>
      <QnAStatusTag status={question.status} />
      <ContentUnitTag item={question} />
      <span className="platform-content-detail-hero__meta-separator">·</span>
      <span>{question.displayName || 'Anonimen'}</span>
      {question.createdAt && (
        <>
          <span className="platform-content-detail-hero__meta-separator">·</span>
          <time dateTime={question.createdAt.toISOString()}>{formatDate(question.createdAt)}</time>
        </>
      )}
    </>
  );

  const heroActions = showCloseAction ? (
    <Button
      variant="outline-secondary"
      size="sm"
      className="qna-detail__close-action"
      onClick={handleClose}
      disabled={closing}
    >
      Zaključi vprašanje
    </Button>
  ) : null;

  return (
    <article className={premiumDetailClassName(PREMIUM_MODULE_IDS.QNA, 'qna-detail platform-content-detail')}>
      <div className="platform-content-detail-shell">
        {useHero ? (
          <ContentDetailHero
            title={question.title}
            meta={heroMeta}
            actions={heroActions}
            variant="qna"
          >
            {extendedBody && (
              extendedBody.isHtml ? (
                <RichTextBlock
                  html={toDisplayHtml(extendedBody.content)}
                  className="qna-detail__question-body qna-detail__question-body--hero"
                />
              ) : (
                <PlainTextBlock
                  text={extendedBody.content}
                  className="qna-detail__question-body qna-detail__question-body--hero"
                />
              )
            )}
          </ContentDetailHero>
        ) : null}

        {!useHero && (extendedBody || showCloseAction) && (
        <div className="platform-content-detail-card qna-detail__question-card">
          <div className="platform-content-detail__body qna-detail__ticket-body">
            <div className="qna-detail__ticket-head">
              <div className="qna-detail__ticket-meta">
                <QnAStatusTag status={question.status} />
                <ContentUnitTag item={question} />
                <span className="qna-detail__ticket-meta-text">
                  {question.displayName || 'Anonimen'}
                  {question.createdAt && (
                    <>
                      <span className="qna-detail__ticket-meta-separator">·</span>
                      <time dateTime={question.createdAt.toISOString()}>
                        {formatDate(question.createdAt)}
                      </time>
                    </>
                  )}
                </span>
              </div>
              {showCloseAction && (
                <Button
                  variant="outline-secondary"
                  size="sm"
                  className="qna-detail__close-action"
                  onClick={handleClose}
                  disabled={closing}
                >
                  Zaključi vprašanje
                </Button>
              )}
            </div>

            {extendedBody && (
              extendedBody.isHtml ? (
                <RichTextBlock html={toDisplayHtml(extendedBody.content)} className="qna-detail__question-body" />
              ) : (
                <PlainTextBlock text={extendedBody.content} className="qna-detail__question-body" />
              )
            )}
          </div>
        </div>
        )}

        {showCloseReminder && (
          <CloseReminderNotice />
        )}

        {showConversation && (
          <div className="platform-content-detail-card qna-detail__conversation qna-detail__conversation--premium">
            <div className="platform-content-detail-card__header qna-detail__conversation-header">
              <h2 className="platform-content-detail-card__title">Potek pogovora</h2>
              {followUpCount > 0 && (
                <span className="qna-detail__conversation-count">
                  {followUpCount}
                  {' '}
                  {followUpCount === 1 ? 'dodatno vprašanje' : followUpCount === 2 ? 'dodatni vprašanji' : 'dodatna vprašanja'}
                </span>
              )}
            </div>
            <div className="qna-detail__timeline">
              {conversationTimeline.map((entry, index) => {
                const entryKey = `${entry.source}-${entry.id}`;
                const isFollowUp = entry.type === QNA_ANSWER_TYPE.FOLLOWUP;
                return (
                  <ThreadEntry
                    key={entryKey}
                    entry={entry}
                    followUpIndex={isFollowUp ? followUpIndexByKey.get(entryKey) : null}
                    onEdit={
                      entry.type === QNA_ANSWER_TYPE.OFFICIAL && canEditOfficial
                        ? () => setEditingAnswer(entry)
                        : null
                    }
                    isLast={!showPendingInTimeline && index === conversationTimeline.length - 1}
                  />
                );
              })}
              {showPendingInTimeline && (
                <PendingAnswerNotice isAuthor={isAuthor} hasFollowUps />
              )}
            </div>
          </div>
        )}

        {showPendingStandalone && (
          <PendingAnswerNotice isAuthor={isAuthor} standalone />
        )}

        {showOfficialCard && !editingAnswer && (
          <OfficialAnswerCard
            answer={officialAnswer}
            onEdit={canEditOfficial ? () => setEditingAnswer(officialAnswer) : null}
            useHero={useHero}
          />
        )}

        {showFollowUpForm && !editingAnswer && (
          <QnAFollowUpForm questionId={questionId} />
        )}

        {editingAnswer && (
          <QnAAnswerForm
            questionId={questionId}
            question={question}
            editingAnswer={editingAnswer}
            onCancel={() => setEditingAnswer(null)}
            onPublished={() => setEditingAnswer(null)}
          />
        )}

        {showAnswerForm && (
          <QnAAnswerForm
            questionId={questionId}
            question={question}
            isConversationReply={isConversationReply}
          />
        )}
      </div>
    </article>
  );
}

export default QnADetail;
